/**
 * Förstaparts samtyckeshantering (GDPR/LEK + Google Consent Mode v2).
 *
 * Ersätter den tidigare tredjeparts-CMP:n. Valet lagras i en förstapartscookie
 * och speglas till Google Consent Mode via gtag('consent','update', ...).
 */

export const CONSENT_VERSION = 1 as const;
export const CONSENT_COOKIE = "tb_consent";

export interface ConsentState {
  version: 1;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  ts: string;
  id: string;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    openConsentSettings?: () => void;
  }
}

function uuidv4(): string {
  const c = typeof crypto !== "undefined" ? crypto : undefined;
  if (c && "randomUUID" in c) return c.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function readConsent(): ConsentState | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${CONSENT_COOKIE}=`));
  if (!match) return null;
  try {
    const raw = decodeURIComponent(match.slice(CONSENT_COOKIE.length + 1));
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (parsed.version !== CONSENT_VERSION) return null;
    return {
      version: CONSENT_VERSION,
      necessary: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      ts: typeof parsed.ts === "string" ? parsed.ts : new Date().toISOString(),
      id: typeof parsed.id === "string" && parsed.id ? parsed.id : uuidv4(),
    };
  } catch {
    return null;
  }
}

function writeCookie(state: ConsentState) {
  const value = encodeURIComponent(JSON.stringify(state));
  const secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${CONSENT_COOKIE}=${value}; Max-Age=${60 * 60 * 24 * 365}; Path=/; SameSite=Lax${secure}`;
}

export function saveConsent(partial: { analytics: boolean; marketing: boolean }): ConsentState {
  const existing = readConsent();
  const state: ConsentState = {
    version: CONSENT_VERSION,
    necessary: true,
    analytics: partial.analytics,
    marketing: partial.marketing,
    ts: new Date().toISOString(),
    id: existing?.id ?? uuidv4(),
  };
  writeCookie(state);
  return state;
}

/** Skickar valet till Google Consent Mode och meddelar resten av appen. */
export function applyConsent(state: ConsentState) {
  if (typeof window === "undefined") return;
  const payload = {
    analytics_storage: state.analytics ? "granted" : "denied",
    ad_storage: state.marketing ? "granted" : "denied",
    ad_user_data: state.marketing ? "granted" : "denied",
    ad_personalization: state.marketing ? "granted" : "denied",
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gtag = (window as any).gtag as ((...args: unknown[]) => void) | undefined;
  if (typeof gtag === "function") {
    gtag("consent", "update", payload);
  } else {
    window.dataLayer = window.dataLayer || [];
    // gtag.js känner bara igen arguments-objekt, aldrig en vanlig array.
    function pushArgs() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    }
    (pushArgs as (...args: unknown[]) => void)("consent", "update", payload);
  }

  window.dispatchEvent(new CustomEvent("tb:consent", { detail: state }));
}

export function hasConsent(category: "analytics" | "marketing"): boolean {
  const state = readConsent();
  if (!state) return false;
  return category === "analytics" ? state.analytics : state.marketing;
}

/** Läser cookien vid sidladdning och speglar valet till Consent Mode. */
export function initConsent(): ConsentState | null {
  const state = readConsent();
  if (state) applyConsent(state);
  return state;
}

export function openConsentSettings() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("tb:consent-open"));
}

export function registerConsentSettingsGlobal() {
  if (typeof window === "undefined") return;
  window.openConsentSettings = openConsentSettings;
}

export function logConsentEvent(state: ConsentState) {
  if (typeof window === "undefined") return;
  try {
    const url = import.meta.env['VITE_SUPABASE_URL'];
    const key = import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY'];
    if (!url || !key) return;
    void fetch(`${url}/rest/v1/consent_events`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: key,
        Authorization: `Bearer ${key}`,
        Prefer: "return=minimal",
      },
      body: JSON.stringify({
        consent_id: state.id,
        analytics: state.analytics,
        marketing: state.marketing,
        banner_version: CONSENT_VERSION,
        user_agent: navigator.userAgent,
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* loggning får aldrig blockera bannern */
  }
}
